import { useEffect, useMemo, useState } from "react";
import type { OperationsIntakeItemDto, OperationsIntakeItemsDto, OperationsIntakeSourceKey, OperationsLensKey } from "@/api/endpoints";
import { BrandedLoader } from "@/components/BrandedLoader";
import { TableSkeleton } from "@/components/Skeleton";
import { ATTENTION_CLASS, ATTENTION_LABEL, INTAKE_SOURCE_LABEL, INTAKE_STATE_LABEL, workTypeLabel } from "../operationsLabels";
import { IntakeItemsTable } from "./IntakeItemsTable";

export { intakeReference } from "./IntakeItemsTable";
import { FacetGroup, SearchBox } from "./DrawerFilterBar";

interface Props {
  title: string;
  data: OperationsIntakeItemsDto | undefined;
  loading: boolean;
  /** The rows shown belong to the previous filters while the new ones load (placeholder data): dim them and say so. */
  updating?: boolean;
  error: string | null;
  onClose: () => void;
  onRetry: () => void;
}

interface IntakeView {
  attention: string[];
  source: string[];
  state: string[];
  workType: string[];
  q: string;
}

const EMPTY_VIEW: IntakeView = { attention: [], source: [], state: [], workType: [], q: "" };

function matches(item: OperationsIntakeItemDto, view: IntakeView): boolean {
  if (view.attention.length > 0 && !view.attention.includes(item.attention)) return false;
  if (view.source.length > 0 && !view.source.includes(item.source)) return false;
  if (view.state.length > 0 && !view.state.includes(item.journeyState)) return false;
  if (view.workType.length > 0 && !view.workType.includes(item.workType)) return false;
  if (view.q) {
    const q = view.q.trim().toLowerCase();
    const hay = [item.worker, item.client, item.pendingWith, item.sourceReference, item.status].filter(Boolean).join(" ").toLowerCase();
    if (q && !hay.includes(q)) return false;
  }
  return true;
}

/**
 * Drill-through for the "Request (Intake)" row: the intakes that do not exist in FBO yet. The API
 * returns the whole lensed queue (its cap is well above the queue size), so the chips and the search
 * filter locally and nothing is stored in the URL. A row with a linked pre-verification opens that
 * request in a new tab; the others have nothing in FBO to open yet.
 */
export function IntakeDrawer({ title, data, loading, updating, error, onClose, onRetry }: Props) {
  const [view, setView] = useState<IntakeView>(EMPTY_VIEW);
  const items = useMemo(() => data?.items ?? [], [data]);
  const shown = useMemo(() => items.filter((i) => matches(i, view)), [items, view]);
  const filtered = view.attention.length > 0 || view.source.length > 0 || view.state.length > 0 || view.q !== "";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const stuckAfter = data?.stuckAfterBd ?? 1;
  const blockedAfter = data?.blockedAfterBd ?? 3;
  const cols = 11;
  const typed = (data?.workTypeFacets ?? []).filter((f) => f.count > 0).length > 1;

  return (
    <>
      <div className="ops-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="ops-drawer" role="dialog" aria-modal="true" aria-labelledby="ops-drawer-title">
        <div className="ops-drawer-head">
          <div>
            <h2 id="ops-drawer-title">{title}</h2>
            <div className="meta">
              {data ? `${shown.length}${shown.length < items.length ? ` of ${items.length}` : ""} item${shown.length === 1 ? "" : "s"} · most severe first, then longest waiting` : "Loading…"}
            </div>
          </div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </div>
        <div className="ops-drawer-note">
          Each row is an open intake request: an onboarding that carries the consultant&rsquo;s journey right now (nothing in FBO yet, or a work order that waits for its request),
          or a request of another type (extension, change request, redeployment, termination, other) that is open by its own status and counts on its type&rsquo;s Flow row.{" "}
          &ldquo;Pending with&rdquo; is the assigned user, or Unassigned. &ldquo;Waiting&rdquo; counts business days since the request started (VMS: the e-mail&rsquo;s
          request date). <b>Stuck</b>: nothing on it changed for more than {stuckAfter} business day{stuckAfter === 1 ? "" : "s"}.{" "}
          <b>Blocked</b>: its status has not changed for more than {blockedAfter} business days, even if someone touched it; both can
          hold (Stuck + Blocked). A row with a linked pre-verification opens that request in a new tab; any other row opens the intake itself.
        </div>
        <div className="ops-drawer-filters">
          <FacetGroup
            label="Attention"
            values={data?.attentionFacets ?? []}
            selected={view.attention}
            labelOf={(v) => ATTENTION_LABEL[v as OperationsLensKey] ?? v}
            dotClass={(v) => `att-${ATTENTION_CLASS[v as OperationsLensKey] ?? "unknown"}`}
            onChange={(attention) => setView((s) => ({ ...s, attention }))}
          />
          <FacetGroup
            label="Source"
            values={data?.sourceFacets ?? []}
            selected={view.source}
            labelOf={(v) => INTAKE_SOURCE_LABEL[v as OperationsIntakeSourceKey] ?? v}
            onChange={(source) => setView((s) => ({ ...s, source }))}
          />
          {typed && (
            <FacetGroup
              label="Type"
              values={data?.workTypeFacets ?? []}
              selected={view.workType}
              labelOf={(v) => workTypeLabel(v)}
              onChange={(workType) => setView((s) => ({ ...s, workType }))}
            />
          )}
          <FacetGroup
            label="State"
            values={data?.stateFacets ?? []}
            selected={view.state}
            labelOf={(v) => INTAKE_STATE_LABEL[v] ?? v}
            onChange={(state) => setView((s) => ({ ...s, state }))}
          />
          <SearchBox value={view.q} onChange={(q) => setView((s) => ({ ...s, q }))} placeholder="Worker, client, assignee or reference" />
        </div>
        {filtered && !error && (
          <div className="ops-drawer-status" role="status">
            Showing <b>{shown.length}</b>{shown.length < items.length ? ` of ${items.length}` : ""}
            <button type="button" className="btn btn-text btn-sm" onClick={() => setView(EMPTY_VIEW)}>Clear filters</button>
          </div>
        )}
        {updating && (
          <div className="ops-drawer-status ops-drawer-updating" role="status">
            <BrandedLoader variant="inline" label="Updating…" />
          </div>
        )}
        <div className={`ops-drawer-body${updating ? " is-stale" : ""}`} aria-busy={updating || undefined}>
          {loading && !data ? (
            <TableSkeleton cols={cols} rows={6} />
          ) : error ? (
            <div className="empty">
              <h4>Could not load the intake queue</h4>
              <p>{error}</p>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>Retry</button>
            </div>
          ) : (
            <IntakeItemsTable
              items={shown}
              empty={filtered ? (
                <div className="empty">
                  <h4>No intakes match these filters</h4>
                  <p>Remove a filter, or <button type="button" className="btn btn-text btn-sm" onClick={() => setView(EMPTY_VIEW)}>clear all filters</button>.</p>
                </div>
              ) : (
                <div className="empty"><h4>Nothing here</h4><p>No open intakes in this bucket.</p></div>
              )}
            />
          )}
        </div>
        <div className="ops-drawer-foot">
          {data && data.totalCount > data.cap ? `Showing the first ${data.cap} of ${data.totalCount}; narrow with the chips or the search.` : "Sorted by attention (Blocked, Stuck, Clear), then by waiting."}
        </div>
      </aside>
    </>
  );
}
